// Diese Datei wurde entfernt, da die Provisionslogik ausgelagert wurde.
import React, { useState, useEffect, useCallback } from 'react';
import { Shield, RefreshCw, ArrowLeft } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const LoginView = ({ onLogin, error: loginError, isLoading: isLoggingIn, loadingProgress, appVersion }: any) => {
    const { isDark } = useTheme();

    const [pin, setPin] = useState('');
    const [tipIndex, setTipIndex] = useState(0);
    const canSubmit = pin.length === 4;
    const onboardingTips = [
        'Tipp: Mit Enter kannst du den PIN direkt bestätigen.',
        'Heute im Fokus: Prüfe offene Aufgaben und anstehende Abläufe.',
        'Hinweis: Mit Escape setzt du die PIN-Eingabe zurück.',
    ];

    const handleNumClick = (num: string) => {
        if (pin.length < 4) {
            setPin(prev => prev + num);
        }
    };

    const handleSubmit = useCallback(() => {
        if (pin.length === 4) onLogin(pin);
    }, [pin, onLogin]);

    useEffect(() => {
        if (pin.length === 4) {
            const timer = setTimeout(handleSubmit, 300);
            return () => clearTimeout(timer);
        }
    }, [pin, handleSubmit]);

    useEffect(() => {
        if (loginError) setPin('');
    }, [loginError]);

    useEffect(() => {
        const interval = setInterval(() => {
            setTipIndex((prev) => (prev + 1) % onboardingTips.length);
        }, 3200);
        return () => clearInterval(interval);
    }, [onboardingTips.length]);

    useEffect(() => {
        const handleKeyDown = (e: any) => {
            if (e.key >= '0' && e.key <= '9') handleNumClick(e.key);
            if (e.key === 'Backspace') setPin(prev => prev.slice(0, -1));
            if (e.key === 'Escape') setPin('');
            if (e.key === 'Enter') handleSubmit();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleSubmit]);

    if (isLoggingIn) return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 relative overflow-hidden">
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute -top-24 left-[-10%] h-72 w-72 rounded-full bg-blue-400/15 blur-3xl" />
                <div className="absolute bottom-[-8rem] right-[-10%] h-80 w-80 rounded-full bg-cyan-300/20 blur-3xl" />
            </div>
            <div className="app-glass-panel border border-white/40 dark:border-white/10 p-10 rounded-3xl shadow-[0_20px_50px_rgba(15,23,42,0.18)] flex flex-col items-center w-full max-w-sm animate-fade-in-up relative z-10">
                <div className="relative mb-8">
                    <RefreshCw className="w-12 h-12 animate-spin text-primary" />
                </div>
                <h2 className="text-xl font-bold tracking-tight mb-4 text-slate-900 dark:text-white text-center">Initialisiere App...</h2>
                <div className="w-full rounded-full h-2 mb-4 overflow-hidden bg-white/50 dark:bg-white/10">
                    <div className="bg-primary h-full rounded-full transition-all duration-500 ease-out" style={{ width: `${Math.max(5, loadingProgress)}%` }}></div>
                </div>
                <p className="font-bold text-sm text-slate-500 dark:text-slate-300">{loadingProgress}%</p>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 overflow-hidden relative">
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute -top-20 left-[-8%] w-[32rem] h-[32rem] bg-blue-400/15 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-8rem] right-[-10%] w-[30rem] h-[30rem] bg-cyan-300/20 rounded-full blur-[120px]" />
            </div>

            <div className="w-full max-w-[400px] flex flex-col z-10">
                <div className="flex flex-col items-center justify-center gap-4 mb-8 animate-fade-in-up">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-primary text-white shadow-[0_10px_24px_rgba(47,107,255,0.32)]">
                        <Shield className="w-8 h-8" strokeWidth={2.5} />
                    </div>
                    <div className="text-center">
                        <span className="font-bold text-3xl tracking-tight text-slate-900 dark:text-white">
                            Agentur<span className="text-primary">PRO</span>
                        </span>
                        <p className="text-slate-500 dark:text-slate-300 font-bold uppercase tracking-widest text-[9px] mt-1">Fortschrittliches CRM</p>
                    </div>
                </div>

                <div className="app-glass-panel border border-white/40 dark:border-white/10 p-8 sm:p-10 rounded-3xl shadow-[0_20px_50px_rgba(15,23,42,0.18)] animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                    <h1 className="text-xl font-bold tracking-tight mb-2 text-center text-slate-900 dark:text-white">Willkommen</h1>
                    <p className="mb-8 text-sm text-center text-slate-600 dark:text-slate-300 font-medium">Gib deinen Sicherheitscode ein</p>
                    <div className="mb-6 rounded-xl border border-blue-200/70 bg-blue-50/70 dark:border-blue-300/30 dark:bg-blue-500/10 px-3 py-2 text-[11px] font-medium text-blue-800 dark:text-blue-200 transition-colors">
                        {onboardingTips[tipIndex]}
                    </div>

                    <div className="flex justify-center gap-3 mb-10">
                        {[0, 1, 2, 3].map((idx) => (
                            <div
                                key={idx}
                                className={`w-3.5 h-3.5 rounded-full transition-all duration-300 border-2 ${idx < pin.length
                                    ? 'bg-primary border-primary scale-110 shadow-sm'
                                    : 'bg-white/60 dark:bg-white/10 border-slate-300 dark:border-white/20'
                                    }`}
                            />
                        ))}
                    </div>

                    {loginError && (
                        <div className="text-[11px] font-bold p-3 rounded-xl mb-6 text-center bg-red-50 text-red-600 border border-red-100 animate-shake">
                            {loginError}
                        </div>
                    )}

                    <div className="grid grid-cols-3 gap-3 mb-8">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                            <button
                                key={num}
                                type="button"
                                onClick={() => handleNumClick(String(num))}
                                className={`h-14 rounded-2xl text-xl font-bold border transition-all shadow-sm active:scale-95 ${
                                    isDark
                                        ? 'bg-white/10 border-white/15 text-white hover:bg-white/15 hover:border-blue-300/40'
                                        : 'bg-white/80 border-slate-200 text-slate-700 hover:border-primary/60 hover:bg-[#F8FAFF]'
                                }`}
                            >
                                {num}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => setPin('')}
                            className={`h-14 rounded-2xl text-xs font-bold transition-all flex items-center justify-center ${
                                isDark ? 'text-red-300 hover:bg-red-400/10' : 'text-red-500 hover:bg-red-50'
                            }`}
                        >
                            LEEREN
                        </button>
                        <button
                            type="button"
                            onClick={() => handleNumClick('0')}
                            className={`h-14 rounded-2xl text-xl font-bold border transition-all shadow-sm active:scale-95 ${
                                isDark
                                    ? 'bg-white/10 border-white/15 text-white hover:bg-white/15 hover:border-blue-300/40'
                                    : 'bg-white/80 border-slate-200 text-slate-700 hover:border-primary/60 hover:bg-[#F8FAFF]'
                            }`}
                        >
                            0
                        </button>
                        <button
                            type="button"
                            onClick={() => setPin(prev => prev.slice(0, -1))}
                            className={`h-14 rounded-2xl transition-all flex items-center justify-center ${
                                isDark ? 'hover:bg-white/10 text-slate-300' : 'hover:bg-slate-50 text-slate-400'
                            }`}
                        >
                            <ArrowLeft size={20} strokeWidth={2.5} />
                        </button>
                    </div>

                    <button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className={`w-full py-4 rounded-2xl font-bold text-sm tracking-wide transition-all ${canSubmit ? 'bg-primary text-white shadow-[0_10px_22px_rgba(47,107,255,0.25)] hover:bg-[#1d59ff] active:scale-[0.98]' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                    >
                        EINLOGGEN
                    </button>
                </div>

                <div className="mt-8 text-center flex flex-col items-center gap-3 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                    <div className="flex items-center gap-2 text-slate-500 dark:text-slate-300 font-bold text-[9px] uppercase tracking-widest">
                        <Shield size={10} />
                        <span>Sicher verschlüsselt</span>
                    </div>
                    <div className="text-[10px] text-slate-500 dark:text-slate-300 font-medium">
                        Version {appVersion}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginView;
